// src/sectors/pilates/members/components/SmsSendModal.jsx
import React, { useEffect, useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Divider from '@mui/material/Divider';
import Icon from '@mui/material/Icon';
import Alert from '@mui/material/Alert';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';

function normalizeTrPhone(v) {
  const s = (v ?? '').toString().replace(/[^0-9]/g, '');
  if (!s) return '';
  // 5xxxxxxxxx -> 05xxxxxxxxx
  if (s.length === 10 && s.startsWith('5')) return `0${s}`;
  // 90... gelirse sadeleştir (opsiyonel)
  if (s.length === 12 && s.startsWith('90') && s[2] === '5') return `0${s.slice(2)}`;
  return s;
}

function capCompanyName(name, max = 20) {
  const t = (name ?? '').toString().trim();
  if (!t) return '';
  const c = t.charAt(0).toUpperCase() + t.slice(1);
  return c.length > max ? `${c.slice(0, max - 1)}…` : c;
}

function normalizeHttpError(e) {
  // http wrapper'ına göre en yaygın şekilleri toparlar
  return (
    e?.response?.data?.message ||
    (Array.isArray(e?.response?.data?.errors) ? e.response.data.errors[0] : null) ||
    e?.response?.data?.error ||
    e?.data?.message ||
    e?.message ||
    'SMS gönderilemedi.'
  );
}

export function SmsSendModal({
  open,
  onClose,
  member, // { id, fullName, phone }
  companyName,
  defaultMessage = '',
  onSend, // async ({ memberId, phone, message }) => Promise
}) {
  function safeText(v, fallback = '—') {
    const s = (v ?? '').toString().trim();
    return s.length ? s : fallback;
  }

  function resolveMemberName(m) {
    if (!m) return '—';
    // en yaygın alanlar
    if (m.fullName) return safeText(m.fullName);
    if (m.name) return safeText(m.name);

    // first/last
    const fn = (m.firstName ?? '').toString().trim();
    const ln = (m.lastName ?? '').toString().trim();
    const joined = `${fn} ${ln}`.trim();
    if (joined) return joined;

    // nested/fallback
    if (m.memberName) return safeText(m.memberName);
    if (m.original?.fullName) return safeText(m.original.fullName);

    return '—';
  }

  const memberName = useMemo(
    () => resolveMemberName(member),
    [
      member?.fullName,
      member?.name,
      member?.firstName,
      member?.lastName,
      member?.memberName,
      member?.original?.fullName,
    ]
  );
  const memberId = member?.id ?? null;

  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState(defaultMessage || '');
  const [saving, setSaving] = useState(false);
  const [sendErr, setSendErr] = useState(null);

  // confirm dialog
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPhone(normalizeTrPhone(member?.phone));
    setMessage(defaultMessage || '');
    setConfirmOpen(false);
    setSaving(false);
    setSendErr(null);
  }, [open, member?.phone, defaultMessage]);

  const maxLen = 150;
  const msgLen = message.length;
  const remaining = maxLen - msgLen;

  const phoneOk = phone.length === 11 && phone.startsWith('05');
  const msgOk = message.trim().length > 0 && msgLen <= maxLen;

  const canSubmit = !!memberId && phoneOk && msgOk && !saving;

  async function handleConfirmSend() {
    if (!canSubmit) return;

    setSaving(true);
    setSendErr(null);

    try {
      const res = await onSend?.({
        memberId,
        phone,
        message: message.trim(),
      });

      if (res?.ok === false) {
        setSendErr(res.error || 'SMS gönderilemedi.');
        return;
      }

      if (res.ok === false) {
        setSendErr(res.error || 'SMS gönderilemedi.');
        return;
      }

      setConfirmOpen(false);
      onClose?.();
    } catch (e) {
      setSendErr(normalizeHttpError(e));
    } finally {
      setSaving(false);
    }
  }

  const brand = capCompanyName(companyName, 20);

  return (
    <>
      {/* ✅ Ana dialog */}
      <Dialog
        open={open}
        onClose={saving ? undefined : onClose}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: '18px', overflow: 'hidden' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <MDBox display="flex" alignItems="center" gap={1}>
            <Icon sx={{ fontSize: 20, opacity: 0.85 }}>sms</Icon>
            <MDTypography fontWeight="bold">SMS Gönder</MDTypography>
          </MDBox>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 2 }}>
          {sendErr ? (
            <Alert severity="error" sx={{ mb: 1.25 }}>
              {sendErr}
            </Alert>
          ) : null}

          {/* Üye */}
          <MDBox sx={{ display: 'grid', gap: 0.5 }}>
            <MDTypography fontWeight="bold" sx={{ fontSize: 14, lineHeight: 1.25 }}>
              {memberName}
            </MDTypography>

            <MDTypography variant="button" sx={{ color: 'text.secondary' }}>
              Kayıtlı Telefon: {member?.phone ? normalizeTrPhone(member.phone) : '—'}
            </MDTypography>
          </MDBox>

          {/* Telefon */}
          <MDBox sx={{ mt: 1.25 }}>
            <MDTypography
              variant="button"
              sx={{ color: 'text.secondary', mb: 0.5, display: 'block' }}
            >
              Telefon
            </MDTypography>

            <MDInput
              value={phone}
              onChange={(e) => setPhone(normalizeTrPhone(e.target.value))}
              placeholder="05xxxxxxxxx"
              fullWidth
              disabled={saving}
              inputProps={{ inputMode: 'numeric', maxLength: 16 }}
            />

            {!phoneOk && phone.length > 0 && (
              <MDTypography
                variant="caption"
                sx={{ color: 'error.main', mt: 0.5, display: 'block' }}
              >
                Telefon formatı geçersiz. Örn: 05xxxxxxxxx
              </MDTypography>
            )}
          </MDBox>

          {/* Mesaj */}
          <MDBox sx={{ mt: 1.25 }}>
            <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
              <MDTypography variant="button" sx={{ color: 'text.secondary' }}>
                Mesaj
              </MDTypography>

              <MDTypography
                variant="caption"
                sx={{ color: remaining < 0 ? 'error.main' : 'text.secondary' }}
              >
                {msgLen}/{maxLen}
              </MDTypography>
            </MDBox>

            <MDInput
              value={message}
              onChange={(e) => {
                const v = e.target.value ?? '';
                if (v.length <= maxLen) setMessage(v);
                else setMessage(v.slice(0, maxLen));
              }}
              placeholder={
                brand
                  ? `Örn: Hatırlatma: Yarınki seansınız için katılım durumunuzu seçin: ... • ${brand}`
                  : 'Mesajınızı yazın...'
              }
              fullWidth
              disabled={saving}
              multiline
              minRows={4}
            />

            {message.trim().length === 0 && (
              <MDTypography
                variant="caption"
                sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}
              >
                Mesaj boş olamaz.
              </MDTypography>
            )}
          </MDBox>
        </DialogContent>

        <DialogActions sx={{ p: 2, pt: 1.25, gap: 1 }}>
          <MDButton
            variant="outlined"
            color="dark"
            onClick={onClose}
            disabled={saving}
            sx={{ borderRadius: '12px' }}
          >
            Vazgeç
          </MDButton>

          <MDButton
            variant="gradient"
            color="dark"
            onClick={() => setConfirmOpen(true)}
            disabled={!canSubmit}
            sx={{ borderRadius: '12px' }}
          >
            Gönder
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* ✅ Onay dialog */}
      <Dialog
        open={confirmOpen}
        onClose={saving ? undefined : () => setConfirmOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: '18px', overflow: 'hidden' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <MDBox display="flex" alignItems="center" gap={1}>
            <Icon sx={{ fontSize: 20, opacity: 0.85 }}>help_outline</Icon>
            <MDTypography fontWeight="bold">Onay</MDTypography>
          </MDBox>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 2 }}>
          <MDTypography variant="button" sx={{ color: 'text.secondary' }}>
            Mesajınız <b>{memberName}</b> kişisine <b>{phone}</b> numarasına şu şekilde
            gönderilecektir:
          </MDTypography>

          <MDBox
            sx={{
              mt: 1.25,
              p: 1.25,
              borderRadius: '12px',
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.default',
              whiteSpace: 'pre-wrap',
              fontSize: 13,
              lineHeight: 1.35,
            }}
          >
            {message.trim()}
          </MDBox>
        </DialogContent>

        <DialogActions sx={{ p: 2, pt: 1.25, gap: 1 }}>
          <MDButton
            variant="outlined"
            color="dark"
            onClick={() => setConfirmOpen(false)}
            disabled={saving}
            sx={{ borderRadius: '12px' }}
          >
            Geri
          </MDButton>

          <MDButton
            variant="gradient"
            color="dark"
            onClick={handleConfirmSend}
            disabled={saving || !canSubmit}
            sx={{ borderRadius: '12px' }}
          >
            Onayla & Gönder
          </MDButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
